import { useEffect, useMemo, useRef, useState } from 'react';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountVariableDefinition } from 'dashboard/models/account-variable-definition';
import { useLazyResolveVariableQuery } from 'dashboard/redux/api/accounts';
import { useLazyRTKErrorHandler } from 'lib/hooks/useRTKErrorHandler';

export function useAccountDataContext(
  accountToken: string, // account to resolve variables for
  variableDefinitions: AccountVariableDefinition[], // variables to be resolved
) {
  const [trigger] = useLazyResolveVariableQuery();
  const { handleUseRTKError } = useLazyRTKErrorHandler();
  const [variableLoadingMap, setVariableLoadingMap] = useState(
    variableDefinitions.reduce(
      (acc, variableDefinition) => ({
        ...acc,
        [variableDefinition.id]: true,
      }),
      {},
    ),
  );
  const isLoading = Object.values(variableLoadingMap).some((loading) => loading);
  const [dataContext, setDataContext] = useState(
    variableDefinitions.reduce(
      (acc, variableDefinition) => ({
        ...acc,
        [variableDefinition.id]: null,
        [variableDefinition.attributes['variable-definition'].key]: null,
      }),
      {},
    ),
  );

  const resolveDataContextPromise = useRef<((value: any) => void) | null>(null);
  const rejectDataContextPromise = useRef<((reason?: any) => void) | null>(null);
  const dataContextPromise = useMemo<Promise<any>>(() => {
    return new Promise((resolve, reject) => {
      resolveDataContextPromise.current = resolve;
      rejectDataContextPromise.current = reject;
    });
  }, []);

  const resolveVariable = async (variableDefinition: AccountVariableDefinition) => {
    const response = await trigger({
      accountToken,
      variableDefinitionToken: variableDefinition.id,
    });

    if (response.error) {
      handleUseRTKError({
        error: response.error,
        onError: () => ({
          message:
            'An unexpected error occurred while loading the account. Some data may be unavailable.',
          level: MessageType.Notice,
        }),
      });
      throw new Error('Error resolving variable');
    } else {
      return response.data;
    }
  };

  const resolveVariableDefinitions = async () => {
    const variablePromises: Promise<any>[] = [];
    const resolveDataContext: Record<string, any> = {};

    variableDefinitions.forEach((variableDefinition) => {
      const variablePromise = resolveVariable(variableDefinition).then((variable) => {
        setDataContext((prev) => ({
          ...prev,
          [variableDefinition.id]: variable,
          [variableDefinition.attributes['variable-definition'].key]: variable,
        }));

        resolveDataContext[variableDefinition.id] = variable;
        resolveDataContext[variableDefinition.attributes['variable-definition'].key] = variable;

        setVariableLoadingMap((prev) => ({ ...prev, [variableDefinition.id]: false }));
      });

      variablePromises.push(variablePromise);
    });
  };

  useEffect(() => {
    if (!isLoading) {
      resolveDataContextPromise.current?.(dataContext);
    }
  }, [dataContext, isLoading]);

  useEffect(() => {
    resolveVariableDefinitions();
  }, []);

  return { dataContext, dataContextPromise, isLoading };
}

interface DataContextProviderProps {
  accountVariableDefinitions: AccountVariableDefinition[];
  accountId: string;
  children: (data: any) => React.ReactNode;
}

export function AccountDataContextProvider({
  accountVariableDefinitions,
  accountId,
  children,
}: DataContextProviderProps) {
  const { dataContext } = useAccountDataContext(accountId, accountVariableDefinitions);

  return <>{children(dataContext)}</>;
}
